/**
 * Support-ticket SLA computation (v2-C).
 *
 * First-response SLA by triage severity (owner defaults, 24×7 wall-clock for v1):
 *   high = 4h, medium = 24h, low = 72h.
 * "First response" = the first support/admin message on the ticket (not a reporter
 * reply). Pure + deterministic (now is injectable) so it's unit-testable.
 */
export type SlaState = 'ok' | 'at_risk' | 'breached' | 'met';

export interface SlaResult {
  state: SlaState;
  label: string;
}

const FIRST_RESPONSE_TARGET_HOURS: Record<string, number> = { high: 4, medium: 24, low: 72 };
const HOUR = 3_600_000;

interface SlaArgs {
  createdAt?: string;
  severity?: string;
  status?: string;
  messages?: { author_is_reporter?: boolean; created_at?: string }[];
  now?: number;
}

export function firstResponseSla(args: SlaArgs): SlaResult | null {
  const { createdAt, severity, status, messages } = args;
  if (!createdAt) return null;
  const created = new Date(createdAt).getTime();
  if (isNaN(created)) return null;

  const sev = (severity || 'medium').toLowerCase();
  const targetMs = (FIRST_RESPONSE_TARGET_HOURS[sev] ?? FIRST_RESPONSE_TARGET_HOURS.medium) * HOUR;

  // Earliest support/admin message (a reporter's own reply doesn't count as a response).
  const firstResp = (messages || [])
    .filter((m) => m.author_is_reporter === false && m.created_at)
    .map((m) => new Date(m.created_at as string).getTime())
    .filter((t) => !isNaN(t))
    .sort((a, b) => a - b)[0];

  if (firstResp != null) {
    return (firstResp - created) <= targetMs
      ? { state: 'met', label: 'SLA met' }
      : { state: 'met', label: 'SLA met late' };
  }

  // No support response yet.
  if ((status || 'open') === 'resolved') return { state: 'met', label: 'SLA met' };

  const now = args.now ?? Date.now();
  const elapsed = now - created;
  if (elapsed > targetMs) {
    const overH = Math.max(1, Math.floor((elapsed - targetMs) / HOUR));
    return { state: 'breached', label: `SLA breached ${overH}h` };
  }
  const leftH = Math.max(1, Math.ceil((targetMs - elapsed) / HOUR));
  if (elapsed > 0.8 * targetMs) return { state: 'at_risk', label: `SLA ${leftH}h left` };
  return { state: 'ok', label: `SLA ${leftH}h left` };
}
